import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Newspaper,
    FileText,
    Trophy,
    Calendar,
    BarChart3,
    ListOrdered,
    Radio,
    Rss,
} from "lucide-react";
import ContentHubSearch from "@/components/ContentHubSearch";
import DiscoverWidget from "@/components/DiscoverWidget";

const SPORT_TABS = [
    { id: "all", name: "All Sports" },
    { id: "football", name: "Football" },
    { id: "basketball", name: "Basketball" },
    { id: "boxing", name: "Boxing" },
    { id: "athletics", name: "Athletics" },
    { id: "racing", name: "Racing / F1" },
];

const FOOTBALL_COMPS = [
    { id: "epl", name: "Premier League" },
    { id: "laliga", name: "La Liga" },
    { id: "serie_a", name: "Serie A" },
    { id: "bundesliga", name: "Bundesliga" },
    { id: "ligue1", name: "Ligue 1" },
    { id: "ucl", name: "UEFA Champions League" },
    { id: "uel", name: "Europa League" },
    { id: "world_cup", name: "FIFA World Cup" },
    { id: "afcon", name: "AFCON" },
    { id: "caf_cl", name: "CAF Champions League" },
    { id: "nbc_premier", name: "NBC Premier League (TZ)" },
];

const BASKETBALL_COMPS = [{ id: "nba", name: "NBA" }];

const ContentHub = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState({
        pending_feed: 0,
        articles: 0,
        drafts: 0,
        published: 0,
        matches: 0,
        scheduled: 0,
        finished: 0,
    });
    const [selectedSport, setSelectedSport] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStats = async () => {
        try {
            const authH = { Authorization: `Bearer ${token}` };
            const [feedResp, draftResp, pubResp, schedResp, finResp] = await Promise.all([
                axios.get(`${API}/admin/external-news?limit=1&status=pending`, { headers: authH }).catch(() => ({ data: { total: 0 } })),
                axios.get(`${API}/content?status=draft&limit=1`, { headers: authH }).catch(() => ({ data: [] })),
                axios.get(`${API}/content?status=published&limit=1`, { headers: authH }).catch(() => ({ data: [] })),
                axios.get(`${API}/matches?status=scheduled&limit=1&enabled_only=false`).catch(() => ({ data: [] })),
                axios.get(`${API}/matches?status=finished&limit=1&enabled_only=false`).catch(() => ({ data: [] })),
            ]);
            // Lightweight counts (list endpoints don't return total, so we just show sampled values + "ingested total")
            setStats({
                pending_feed: feedResp.data?.total || 0,
                articles: (draftResp.data?.length || 0) + (pubResp.data?.length || 0),
                drafts: draftResp.data?.length || 0,
                published: pubResp.data?.length || 0,
                matches: (schedResp.data?.length || 0) + (finResp.data?.length || 0),
                scheduled: schedResp.data?.length || 0,
                finished: finResp.data?.length || 0,
            });
        } finally {
            setLoading(false);
        }
    };

    const hubCards = [
        {
            id: "news-feed",
            title: "News Feed",
            description:
                "Review articles from BBC, Sky, ESPN, Al Jazeera, CNN, FIFA, CAF. Import and translate to Swahili.",
            icon: Rss,
            color: "text-electric",
            path: "/admin/news-feed",
            stat: `${stats.pending_feed}+`,
            statLabel: "Pending Review",
            testid: "hub-news-feed",
        },
        {
            id: "articles",
            title: "Articles",
            description:
                "All published and draft articles. Edit Swahili titles, bodies, and images. Publish or unpublish.",
            icon: FileText,
            color: "text-blue-400",
            path: "/admin/content",
            stat: `${stats.drafts}+`,
            statLabel: "Drafts",
            testid: "hub-articles",
        },
        {
            id: "results",
            title: "Match Results",
            description:
                "Add, edit, delete match results manually. Toggle visibility. Covers all sports.",
            icon: Trophy,
            color: "text-yellow-400",
            path: "/admin/matches",
            stat: `${stats.finished}+`,
            statLabel: "Finished",
            testid: "hub-results",
        },
        {
            id: "schedules",
            title: "Fixtures",
            description:
                "Upcoming fixtures in East African Time with pre-match preview: analysis, injuries, H2H, AI prediction — all in Swahili.",
            icon: Calendar,
            color: "text-green-400",
            path: "/admin/schedules",
            stat: `${stats.scheduled}+`,
            statLabel: "Upcoming",
            testid: "hub-schedules",
        },
        {
            id: "stats",
            title: "Statistics",
            description:
                "Match-level stats: possession, shots, cards. Edit per-match in the Results page.",
            icon: BarChart3,
            color: "text-purple-400",
            path: "/admin/matches",
            stat: "Live",
            statLabel: "From ESPN",
            testid: "hub-stats",
        },
        {
            id: "standings",
            title: "Standings (Msimamo)",
            description:
                "League tables: position, W-D-L, goal difference, points. Per competition & season.",
            icon: ListOrdered,
            color: "text-red-400",
            path: "/admin/standings",
            stat: "—",
            statLabel: "Msimamo",
            testid: "hub-standings",
        },
    ];

    const sportComps =
        selectedSport === "football" ? FOOTBALL_COMPS : selectedSport === "basketball" ? BASKETBALL_COMPS : [];

    return (
        <AdminLayout title="Content Hub">
            <div className="space-y-6" data-testid="content-hub">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Radio className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">
                                Content Hub
                            </h1>
                            <p className="text-white/50 text-sm">
                                Central command for news, results, schedules, stats and standings — across every sport we cover.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Universal Curation Search */}
                <ContentHubSearch />

                {/* Discover from Web */}
                <DiscoverWidget />

                {/* Sport Tabs */}
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide border-b border-border">
                    {SPORT_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setSelectedSport(tab.id)}
                            data-testid={`hub-sport-${tab.id}`}
                            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                selectedSport === tab.id
                                    ? "border-electric text-electric"
                                    : "border-transparent text-white/60 hover:text-white"
                            }`}
                        >
                            {tab.name}
                        </button>
                    ))}
                </div>

                {/* Competitions strip for selected sport */}
                {sportComps.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white/40 text-xs uppercase tracking-wider mr-2">
                            Competitions:
                        </span>
                        {sportComps.map((c) => (
                            <Badge
                                key={c.id}
                                variant="outline"
                                className="border-border text-white/70 hover:border-electric/50 hover:text-electric cursor-default"
                            >
                                {c.name}
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Hub Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {hubCards.map((c) => (
                        <Link
                            key={c.id}
                            to={c.path}
                            data-testid={c.testid}
                            className="group"
                        >
                            <Card className="bg-paper border-border p-6 h-full hover:border-electric/60 hover:shadow-glow transition-all">
                                <div className="flex items-start justify-between mb-4">
                                    <div
                                        className={`w-12 h-12 rounded-lg bg-subtle flex items-center justify-center ${c.color}`}
                                    >
                                        <c.icon className="h-6 w-6" />
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-heading font-black text-white">
                                            {loading ? "..." : c.stat}
                                        </div>
                                        <div className="text-white/40 text-[10px] uppercase tracking-wider">
                                            {c.statLabel}
                                        </div>
                                    </div>
                                </div>
                                <h3 className="font-heading font-bold text-lg text-white mb-2 group-hover:text-electric transition-colors">
                                    {c.title}
                                </h3>
                                <p className="text-white/60 text-sm leading-relaxed">
                                    {c.description}
                                </p>
                            </Card>
                        </Link>
                    ))}
                </div>

                {/* Trusted Sources Info */}
                <Card className="bg-paper border-border p-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded bg-electric/10 flex items-center justify-center">
                            <Newspaper className="h-5 w-5 text-electric" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-white font-heading font-bold text-base mb-1">
                                Auto-Publish Trusted Sources
                            </h3>
                            <p className="text-white/60 text-sm mb-3">
                                Articles from these feeds skip the review queue, get auto-translated (if English) and publish instantly to the public site every 15 minutes.
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge className="bg-electric/20 text-electric border-electric/30">
                                    BBC Swahili (native Swahili)
                                </Badge>
                                <Badge className="bg-electric/20 text-electric border-electric/30">
                                    BBC Football
                                </Badge>
                                <Badge className="bg-electric/20 text-electric border-electric/30">
                                    CNN Sport
                                </Badge>
                                <Badge className="bg-electric/20 text-electric border-electric/30">
                                    Al Jazeera
                                </Badge>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default ContentHub;
